/** 解析 collection 接口 outcomes / outcomePrices 字段（JSON 字符串或 CSV） */
export function parseMarketOutcomes(field) {
  if (!field) return [];
  try {
    const parsed = typeof field === 'object' ? field : JSON.parse(field);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item ?? '').trim()).filter(Boolean);
    }
  } catch {
    /* fallback */
  }
  return String(field)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseOutcomePricesRaw(field) {
  if (field == null || field === '') return null;
  if (typeof field === 'object') return field;
  try {
    return JSON.parse(field);
  } catch {
    return null;
  }
}

/**
 * 解析 outcomePrices 并与 outcomes 按下标对齐
 * outcomes = 选项，outcomePrices = 同位置价格/结算值（如 ["Yes","No"] + ["1","0"] → Yes 为结果）
 */
export function parseOutcomePricesAligned(outcomesField, outcomePricesField) {
  const labels = parseMarketOutcomes(outcomesField);
  const pricesRaw = parseOutcomePricesRaw(outcomePricesField);
  if (!labels.length || !Array.isArray(pricesRaw)) return null;

  const prices = pricesRaw.map((value) => Number(value));
  return { labels, prices };
}

/** 已结算市场：outcomePrices 与 outcomes 按下标对应，值为 1（或 closed 后近 1）的项即为结果 */
export function resolveWinningOutcome(outcomesField, outcomePricesField) {
  const aligned = parseOutcomePricesAligned(outcomesField, outcomePricesField);
  if (!aligned) return '';

  const { labels, prices } = aligned;
  let winIndex = prices.findIndex((price) => Number(price) === 1);
  if (winIndex < 0) {
    // PM crypto short-cycle often parks at 0.995 before exact 0/1 lands in our API
    let bestIdx = -1;
    let best = 0;
    for (let i = 0; i < prices.length; i += 1) {
      const n = Number(prices[i]);
      if (!Number.isFinite(n) || n < 0.99) continue;
      if (bestIdx >= 0 && n === best) {
        bestIdx = -1;
        break;
      }
      if (bestIdx < 0 || n > best) {
        bestIdx = i;
        best = n;
      }
    }
    winIndex = bestIdx;
  }
  if (winIndex < 0 || !labels[winIndex]) return '';
  return String(labels[winIndex]).trim();
}

/** 市场是否已结算（Resolved / Cancelled / closed） */
export function isMarketResolved(market, collection) {
  if (Number(market?.closed ?? collection?.closed) === 1) return true;
  const status = String(market?.marketStatus ?? collection?.marketStatus ?? '')
    .trim()
    .toLowerCase();
  return status === 'resolved' || status === 'cancelled';
}

/** 详情加载后保留结算 outcomePrices，避免价格轮询覆盖 */
export function preserveSettlementOutcomePrices(data) {
  if (!data || typeof data !== 'object') return;
  const markets = Array.isArray(data.markets) ? data.markets : [];
  for (const market of markets) {
    if (!isMarketResolved(market, data)) continue;
    const prices = market?.outcomePrices;
    if (prices != null && prices !== '') {
      market.settlementOutcomePrices = prices;
    }
  }
}

export function parseOutcomePrices(field) {
  return parseMarketOutcomes(field)
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));
}

/** 二元概率对：兼容 0~1 与 0~100 刻度，输出 0~1 */
export function normalizeBinaryProbabilityPair(prices) {
  if (!Array.isArray(prices) || prices.length < 2) return prices;
  let yes = Number(prices[0]);
  let no = Number(prices[1]);
  if (!Number.isFinite(yes) || !Number.isFinite(no)) return prices;

  const sum = yes + no;
  if (sum > 1.5 && sum <= 100.5) {
    yes /= 100;
    no /= 100;
  }
  return [yes, no];
}

/** 二元价格是否互补有效（和 ≈ 1） */
export function isValidBinaryProbabilityPair(prices) {
  const [yes, no] = normalizeBinaryProbabilityPair(prices);
  if (!Number.isFinite(yes) || !Number.isFinite(no)) return false;
  return Math.abs(yes + no - 1) <= 0.08;
}

/** 保留 API 原始小数位：0~1 概率价 → 美分，0~100 刻度原样 */
export function outcomePriceToCents(priceRaw) {
  const str = String(priceRaw ?? '').trim();
  if (!str) return NaN;
  const num = Number(str);
  if (!Number.isFinite(num)) return NaN;

  if (num > 0 && num <= 1) {
    const fracLen = str.includes('.') ? str.split('.')[1].length : 0;
    const centFracLen = Math.max(0, fracLen - 2);
    if (centFracLen === 0) return num * 100;
    const scale = 10 ** centFracLen;
    return Math.round(num * 100 * scale) / scale;
  }

  if (str.includes('.')) {
    const fracLen = str.split('.')[1].length;
    const scale = 10 ** fracLen;
    return Math.round(num * scale) / scale;
  }
  return num;
}

export function clampTradeCents(n) {
  return Math.min(99, Math.max(1, Math.round(Number(n) || 50)));
}

/** 限制在 0.1~99.9，保留 1 位小数（限价步进等交互场景） */
export function normalizeTradeCents(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return 50;
  const scaled = Math.round(num * 10) / 10;
  return Math.min(99.9, Math.max(0.1, scaled));
}

/** 0~1 或 0~100 价格 → 美分（保留 API 精度，不截断） */
export function priceToTradeCents(price) {
  const cents = outcomePriceToCents(price);
  if (!Number.isFinite(cents)) return 50;
  return Math.min(99.9999, Math.max(0.0001, cents));
}

/** 展示用：始终一位小数（不含 ¢ 后缀）；限价输入 / 交易面板选项 */
export function formatCentsDisplayOneDecimal(cents) {
  const num = Number(cents);
  if (!Number.isFinite(num)) return '';
  return (Math.round(num * 10) / 10).toFixed(1);
}

/** 展示用：完整美分，去掉浮点尾零（不含 ¢ 后缀） */
export function formatCentsDisplay(cents) {
  const num = Number(cents);
  if (!Number.isFinite(num)) return '';
  const fixed = num.toFixed(8).replace(/\.?0+$/, '');
  return fixed || '0';
}

/** 内部价格 → API 美元价（0~1 原样；>1 按美分转美元） */
export function tradePriceToDollars(price) {
  const n = Number(price);
  if (!Number.isFinite(n)) return 0;
  if (n > 0 && n <= 1) return n;
  return Math.round(n * 100) / 10000;
}

/** 限价 UI / 下单：美分 → 美元，保留完整精度 */
export function tradeCentsToDollars(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return 0;
  return n / 100;
}

/** 从 API outcomes 数组取 binary 两个原值，不足时用 Yes/No 回退 */
export function getBinaryOutcomeValues(apiOutcomes = []) {
  const list = Array.isArray(apiOutcomes) ? apiOutcomes : [];
  return {
    yes: String(list[0] ?? 'Yes').trim() || 'Yes',
    no: String(list[1] ?? 'No').trim() || 'No',
  };
}

/**
 * 盘口市价成交参考价（美分）：买入取卖一，卖出取买一；
 * 无对应买卖盘时回退 priceCents（当前赔率），供 MARKET 空簿后端降级 LIMIT 挂单。
 * @param {{ askCents?: number, bidCents?: number, priceCents?: number } | null | undefined} outcome
 * @param {'buy'|'sell'|string} side
 * @returns {number|null}
 */
export function resolveMarketOrderPriceCents(outcome, side = 'buy') {
  const isSell =
    String(side ?? '')
      .trim()
      .toLowerCase() === 'sell';
  const ask = Number(outcome?.askCents);
  const bid = Number(outcome?.bidCents);
  const configured = Number(outcome?.priceCents);
  const pick = (n) => (Number.isFinite(n) && n > 0 ? n : null);

  if (isSell) return pick(bid) ?? pick(configured);
  return pick(ask) ?? pick(configured);
}

/**
 * 限价展示 / 默认限价（美分）：
 * - preferConfigured：优先 priceCents（自建=/market/price）
 * - preferTaker：买入→卖一、卖出→买一（PM 对齐官方「买入价」）
 * - 否则（分钟盘）：买入→买一、卖出→卖一；无簿时回退 priceCents
 * @param {{ askCents?: number, bidCents?: number, priceCents?: number } | null | undefined} outcome
 * @param {'buy'|'sell'|string} [side='buy']
 * @param {{ preferConfigured?: boolean, preferTaker?: boolean }} [opts]
 * @returns {number|null}
 */
export function resolveLimitOrderDisplayPriceCents(outcome, side = 'buy', opts = {}) {
  const pick = (n) => (Number.isFinite(n) && n > 0 ? n : null);
  const configured = pick(Number(outcome?.priceCents));
  const ask = pick(Number(outcome?.askCents));
  const bid = pick(Number(outcome?.bidCents));

  if (opts.preferConfigured === true && configured != null) return configured;

  const isSell =
    String(side ?? '')
      .trim()
      .toLowerCase() === 'sell';

  if (opts.preferTaker === true) {
    const taker = isSell ? bid : ask;
    if (taker != null) return taker;
    return configured;
  }

  const maker = isSell ? ask : bid;
  if (maker != null) return maker;

  // 无订单簿时回退详情配置价
  return configured;
}

/**
 * 构建 TradeOrderDialog 用的 binary 选项（含 API outcome 原值）
 * @param {string[]} apiOutcomes
 * @param {number[]} [prices]
 * @param {{
 *   yesLabel?: string,
 *   noLabel?: string,
 *   yesCents?: number,
 *   noCents?: number,
 *   yesAskCents?: number,
 *   yesBidCents?: number,
 *   noAskCents?: number,
 *   noBidCents?: number,
 * }} [labels]
 */
export function buildBinaryTradeOutcomes(apiOutcomes, prices = [], labels = {}) {
  const { yes, no } = getBinaryOutcomeValues(apiOutcomes);

  const optionalCents = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num) || num <= 0) return null;
    return normalizeTradeCents(num);
  };

  const yesAsk = optionalCents(labels.yesAskCents);
  const yesBid = optionalCents(labels.yesBidCents);
  const noAsk = optionalCents(labels.noAskCents);
  const noBid = optionalCents(labels.noBidCents);

  // priceCents：自建限价默认跟详情配置 / `/market/price`；市价走 askCents/bidCents
  const yesCents =
    optionalCents(labels.yesCents) != null
      ? optionalCents(labels.yesCents)
      : Number.isFinite(Number(prices[0]))
      ? priceToTradeCents(prices[0])
      : yesAsk;
  const noCents =
    optionalCents(labels.noCents) != null
      ? optionalCents(labels.noCents)
      : Number.isFinite(Number(prices[1]))
      ? priceToTradeCents(prices[1])
      : Number.isFinite(Number(prices[0]))
      ? priceToTradeCents(1 - Number(prices[0]))
      : noAsk;

  return [
    {
      label: labels.yesLabel ?? yes,
      priceCents: yesCents,
      askCents: yesAsk,
      bidCents: yesBid,
      outcome: yes,
    },
    {
      label: labels.noLabel ?? no,
      priceCents: noCents,
      askCents: noAsk,
      bidCents: noBid,
      outcome: no,
    },
  ];
}

/**
 * 构建 multi 选项（outcome 与 API label 原值一致）
 * 下单价优先 askCents；无卖一则为 null（禁止默认 50¢）
 * @param {Array<{ label: string, percent?: number, outcome?: string, outcomePrice?: number, priceCents?: number, askCents?: number, bidCents?: number }>} options
 */
export function buildMultiTradeOutcomes(options = []) {
  return options.map((opt) => {
    const outcome = String(opt?.outcome ?? opt?.label ?? '').trim();
    const ask =
      opt?.askCents != null && Number.isFinite(Number(opt.askCents)) && Number(opt.askCents) > 0
        ? normalizeTradeCents(opt.askCents)
        : null;
    const bid =
      opt?.bidCents != null && Number.isFinite(Number(opt.bidCents)) && Number(opt.bidCents) > 0
        ? normalizeTradeCents(opt.bidCents)
        : null;

    // priceCents：限价默认跟 outcomePrice；市价另走 askCents
    let priceCents = null;
    if (opt?.priceCents != null && Number.isFinite(Number(opt.priceCents))) {
      const cents = Number(opt.priceCents);
      if (cents > 0)
        priceCents = cents <= 1 ? normalizeTradeCents(cents * 100) : normalizeTradeCents(cents);
    }
    if (
      priceCents == null &&
      opt?.outcomePrice != null &&
      Number.isFinite(Number(opt.outcomePrice))
    ) {
      priceCents = priceToTradeCents(opt.outcomePrice);
    }
    if (priceCents == null) priceCents = ask;

    return {
      label: opt?.label ?? outcome,
      priceCents,
      askCents: ask,
      bidCents: bid,
      outcome: outcome || 'Yes',
    };
  });
}

/**
 * 构建多元市场 TradeOrderDialog 状态（展示全部 outcome，非 Yes/No）
 * @param {Array<{ label: string, percent?: number, outcome?: string }>} multiOptions
 * @param {number} [optionIndex=0] 用户点击的选项下标
 * @param {string} [title=''] 集合/市场标题
 */
export function buildMultiTradeDialogState(multiOptions = [], optionIndex = 0, title = '') {
  const opts = Array.isArray(multiOptions) ? multiOptions : [];
  const idx = Number(optionIndex);
  const initialOutcomeIndex = Number.isFinite(idx) && idx >= 0 && idx < opts.length ? idx : 0;
  return {
    title: String(title || opts[initialOutcomeIndex]?.label || '').trim(),
    contextLine: '',
    marketKind: 'multipleChoice',
    outcomes: buildMultiTradeOutcomes(opts),
    initialOutcomeIndex,
    outcomeCardLayout: 'default',
  };
}
